import clsx from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "outline" | "ghost" | "subtle" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant;
    size?: Size;
    loading?: boolean;
    icon?: ReactNode;
    fullWidth?: boolean;
}

const VARIANTS: Record<Variant, string> = {
    primary:
        // Hover pakai opacity (bukan ungu statis) supaya konsisten di tema gelap.
        "bg-[var(--color-primary)] text-white hover:opacity-90 border border-transparent shadow-sm",
    outline:
        "app-content-card app-text-primary border hover:app-content-subtle-hover",
    ghost: "bg-transparent app-shell-text-muted hover:app-content-subtle-hover border border-transparent",
    subtle: "app-content-subtle app-text-primary border app-shell-border hover:app-content-subtle-hover",
    danger: "bg-[var(--color-error)] text-white hover:opacity-90 border border-transparent",
};

const SIZES: Record<Size, string> = {
    sm: "h-8 px-3 text-xs gap-1.5",
    md: "h-10 px-4 text-sm gap-2",
    lg: "h-12 px-6 text-base gap-2",
};

export function Button({
    variant = "primary",
    size = "md",
    loading = false,
    icon,
    fullWidth = false,
    className,
    disabled,
    children,
    ...rest
}: ButtonProps) {
    return (
        <button
            className={clsx(
                "inline-flex items-center justify-center rounded-xl font-semibold transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/40",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                VARIANTS[variant],
                SIZES[size],
                fullWidth && "w-full",
                className,
            )}
            disabled={disabled || loading}
            {...rest}
        >
            {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
                icon
            )}
            {children}
        </button>
    );
}
